import React, { useState, useEffect } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, MenuItem,
  Card, CardContent, Divider, useTheme, Chip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Save as SaveIcon, 
  ArrowBack,
  Lock as LockIcon,
  ContentCopy as DuplicateIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import axios from '../../../axios';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import AccountSelect from '../../../components/AccountSelect';
import ImportJournalEntryDialog from './ImportJournalEntryDialog';
import JournalEntryPrint from './JournalEntryPrint';
import { format } from 'date-fns';

const JournalEntryForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const branchId  = parseInt(localStorage.getItem('branchId'),  10);
  const isEdit = !!id;
  const isView = location.pathname.includes('/view'); // Check if view mode
  // Get type from URL or default to 1 (Daily)
  const initialType = parseInt(searchParams.get('type')) || 1;

  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    referenceNumber: '',
    entryType: initialType,
    description: '',
    branchId: branchId,
    companyId: companyId,
    entryNumber: '', // Will be displayed as Auto
  });

  const [lines, setLines] = useState([
    { id: 1, accountId: null, description: '', debit: 0, credit: 0 },
    { id: 2, accountId: null, description: '', debit: 0, credit: 0 }
  ]);

  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [accountMap, setAccountMap] = useState({});
  const printRef = React.useRef();

  // Fetch accounts for print mapping
  useEffect(() => {
    chartOfAccountsApi.getAll().then(res => {
        const map = {};
        res.data?.forEach(acc => {
            map[acc.id] = `${acc.code} - ${acc.nameAr}`;
        });
        setAccountMap(map);
    }).catch(err => console.error("Error fetching accounts for print:", err));
  }, []);

  const handlePrint = useReactToPrint({
      contentRef: printRef,
      documentTitle: `Journal_Entry_${formData.entryNumber || 'New'}`,
  });

  const handleImportSuccess = (importedLines) => {
    // Convert imported lines to form structure
    const newLines = importedLines.map((l, index) => ({
        id: Date.now() + index, // unique ID
        accountId: l.accountId,
        description: l.description || '',
        debit: l.debit,
        credit: l.credit
    }));

    // If current lines are just the initial empty ones, replace them. Otherwise append.
    const isInitial = lines.length === 2 && lines.every(l => !l.accountId && !l.debit && !l.credit);
    
    if (isInitial) {
        setLines(newLines);
    } else {
        setLines([...lines, ...newLines]);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchEntry();
    } else {
        fetchNextNumber();
    }
  }, [id, formData.entryType]);

  const fetchNextNumber = async () => {
      try {
          const response = await axios.get('/JournalEntries/next-number', {
              params: {
                  branchId: formData.branchId,
                  entryType: formData.entryType
              }
          });
          setFormData(prev => ({ ...prev, entryNumber: response.data.nextNumber }));
      } catch (error) {
          console.error("Failed to fetch next number", error);
      }
  };

  const fetchEntry = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/JournalEntries/${id}`);
      const entry = response.data;
      setFormData({
        date: format(new Date(entry.date), 'yyyy-MM-dd'),
        referenceNumber: entry.referenceNumber || '',
        entryType: entry.entryType,
        description: entry.description,
        branchId: entry.branchId,
        entryNumber: entry.entryNumber
      });
      setLines(entry.lines.map((l, index) => ({
        id: l.id || index,
        accountId: l.accountId,
        description: l.description,
        debit: l.debit,
        credit: l.credit
      })));
    } catch (error) {
      console.error('Error fetching entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...lines];
    newLines[index][field] = value;
    
    if (field === 'debit' && Number(value) > 0) newLines[index].credit = 0;
    if (field === 'credit' && Number(value) > 0) newLines[index].debit = 0;

    // Auto-balance logic: If selecting an account on a new line (except first line), fill with difference
    if (field === 'accountId' && value && index > 0) {
        // Calculate current imbalance excluding the current line
        let currentDebit = 0;
        let currentCredit = 0;
        
        newLines.forEach((line, idx) => {
            if (idx !== index) {
                currentDebit += Number(line.debit || 0);
                currentCredit += Number(line.credit || 0);
            }
        });

        const diff = currentDebit - currentCredit;
        
        // If difference is positive (Debit > Credit), we need Credit
        if (diff > 0 && !newLines[index].debit && !newLines[index].credit) {
            newLines[index].credit = diff;
            newLines[index].debit = 0;
        }
        // If difference is negative (Credit > Debit), we need Debit
        else if (diff < 0 && !newLines[index].debit && !newLines[index].credit) {
            newLines[index].debit = Math.abs(diff);
            newLines[index].credit = 0;
        }
    }

    setLines(newLines);
  };

  const handleKeyDown = (e, index, field) => {
      if (e.key === 'Enter') {
          e.preventDefault();
          
          if (field === 'credit' && index === lines.length - 1) {
              // If it's the last field of the last line, add a new line
              addLine();
              // Focus logic can be handled here if needed, but new line renders with auto-focus if implemented
          } else {
              // Move to next field
              const formElements = e.target.form.elements;
              const indexInForm = Array.prototype.indexOf.call(formElements, e.target);
              if (indexInForm > -1 && indexInForm < formElements.length - 1) {
                  const nextElement = formElements[indexInForm + 1];
                  if (nextElement && !nextElement.disabled) {
                      nextElement.focus();
                      if (nextElement.select) {
                          nextElement.select(); // Select all text
                      }
                  }
              }
          }
      }
  };

  const handleFocus = (event) => {
      event.target.select();
  };

  const addLine = () => {
    setLines([...lines, { id: Date.now(), accountId: null, description: '', debit: 0, credit: 0 }]);
  };

  const removeLine = (index) => {
    if (lines.length <= 2) return;
    const newLines = lines.filter((_, i) => i !== index);
    setLines(newLines);
  };

  const getTotalDebit = () => lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
  const getTotalCredit = () => lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);
  const getDifference = () => getTotalDebit() - getTotalCredit();

  const handleDuplicate = async () => {
      // 1. Fetch next number for current type
      try {
          const response = await axios.get('/JournalEntries/next-number', {
              params: {
                  branchId: formData.branchId,
                  entryType: formData.entryType
              }
          });
          
          // 2. Set form data for new entry
          setFormData(prev => ({ 
              ...prev, 
              entryNumber: response.data.nextNumber,
              date: format(new Date(), 'yyyy-MM-dd'), // New date
              referenceNumber: '', // Clear reference
              description: prev.description ? `${prev.description} (نسخة)` : ''
          }));

          // 3. Clear lines IDs to treat them as new
          setLines(prevLines => prevLines.map((l, idx) => ({
              ...l,
              id: Date.now() + idx
          })));

          // 4. Update URL to remove ID (switch to create mode)
          navigate('/journal-entries/new', { replace: true });
          
          // Force a re-render or state update to ensure "Create" mode is active effectively
          // Since we navigated, the component might re-mount, but if we stay on same component instance:
          // We need to ensure `isEdit` becomes false. 
          // However, `isEdit` is derived from `useParams`.
          // Navigating to `/new` will remount the component or update params.
          
      } catch (error) {
          console.error("Failed to prepare duplication", error);
      }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Math.abs(getDifference()) > 0.01) {
      alert(t('entriesNotBalanced'));
      return;
    }

    if (lines.some(l => !l.accountId)) {
        alert(t('allLinesMustHaveAccount'));
        return;
    }

    // Auto-fill descriptions
    const finalDescription = formData.description || `${t(entryTypes.find(t => t.value === formData.entryType)?.label)} #${formData.entryNumber}`;
    
    try {
      setLoading(true);
      const payload = {
        branchId: formData.branchId,
        date: formData.date,
        entryType: formData.entryType,
        description: finalDescription,
        referenceNumber: formData.referenceNumber,
        lines: lines.map(l => ({
          accountId: l.accountId,
          description: l.description || finalDescription,
          debit: Number(l.debit),
          credit: Number(l.credit),
          costCenterId: l.costCenterId || null // Ensure this field exists if needed
        }))
      };

      if (isEdit) {
        await axios.put(`/JournalEntries/${id}`, payload);
      } else {
        await axios.post('/JournalEntries', payload);
      }
      navigate('/journal-entries');
    } catch (error) {
      console.error('Error saving entry:', error);
      alert(error.response?.data?.title || error.response?.data || 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const entryTypes = [
    { value: 1, label: t('dailyEntry') },
    { value: 2, label: t('openingEntry') },
    { value: 3, label: t('salesEntry') },
    { value: 4, label: t('salesReturnEntry') },
    { value: 5, label: t('purchaseEntry') },
    { value: 6, label: t('purchaseReturnEntry') },
    { value: 7, label: t('paymentVoucher') },
    { value: 8, label: t('receiptVoucher') },
    { value: 9, label: t('transferEntry') },
    { value: 10, label: t('adjustmentEntry') },
    { value: 11, label: t('depreciationEntry') },
    { value: 12, label: t('payrollEntry') },
    { value: 13, label: t('closingEntry') }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: '100%', mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/journal-entries')} sx={{ mr: 2, bgcolor: 'background.paper' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            {isView ? t('viewEntry') : (isEdit ? t('editEntry') : t('newEntry'))}
          </Typography>
        </Box>
        <Box>
            {(isEdit || isView) && [1, 2].includes(formData.entryType) && (
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<DuplicateIcon />}
                onClick={handleDuplicate}
                sx={{ mr: 2 }}
              >
                {t('duplicateEntry') }
              </Button>
            )}
            {!isView && (
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={() => setImportOpen(true)}
                sx={{ mr: 2 }}
              >
                📥 استيراد من Excel
              </Button>
            )}
            {/* Optional: Status Chip if Edit Mode */}
            <Button
                variant="outlined"
                color="info"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{ mr: 2 }}
            >
                {t('PRINT') }
            </Button>
        </Box>
      </Box>

      <div style={{ display: 'none' }}>
        <JournalEntryPrint 
            ref={printRef} 
            data={{
                ...formData,
                lines: lines.map(l => ({
                    ...l,
                    accountName: accountMap[l.accountId] || ''
                }))
            }} 
            entryTypes={entryTypes} 
        />
      </div>

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Row 1: Basic Info */}
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label={t('entryNumber')}
                  value={isEdit ? formData.entryNumber : t('autoGenerated')}
                  disabled
                  InputProps={{
                    startAdornment: <LockIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />,
                    sx: { bgcolor: 'action.hover' }
                  }}
                />
              </Grid>
              
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label={t('date')}
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  disabled={isView}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  select
                  label={t('entryType')}
                  value={formData.entryType}
                  onChange={(e) => setFormData({ ...formData, entryType: e.target.value })}
                  required
                  disabled // Locked as requested
                  InputProps={{
                    sx: { bgcolor: 'action.hover' }
                  }}
                >
                  {entryTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label={t('referenceNumber')}
                  value={formData.referenceNumber}
                  onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                  placeholder={t('optional')}
                  disabled={isView}
                />
              </Grid>

              {/* Row 2: Description (Now integrated) */}
              <Grid item xs={12} md>
                <TextField
                  fullWidth
                  label={t('description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  // removed required to allow auto-generation
                  multiline
                  rows={1}
                  placeholder={t('enterDescription')}
                  disabled={isView}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ bgcolor: 'primary.main' }}>
                <TableRow>
                  <TableCell width="30%" sx={{ color: 'white', fontWeight: 'bold' }}>{t('account')}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('description')}</TableCell>
                  <TableCell width="10%" sx={{ color: 'white', fontWeight: 'bold' }}>{t('debit')}</TableCell>
                  <TableCell width="10%" sx={{ color: 'white', fontWeight: 'bold' }}>{t('credit')}</TableCell>
                  <TableCell width="5%" sx={{ color: 'white', fontWeight: 'bold' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow key={line.id} hover>
                    <TableCell sx={{ p: 1.5 }}>
                      <AccountSelect
                        value={line.accountId}
                        onChange={(val) => handleLineChange(index, 'accountId', val)}
                        required
                        size="small"
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={line.description}
                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'description')}
                        placeholder={t('lineDescription')}
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1.5 }}>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={line.debit}
                        onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'debit')}
                        onFocus={handleFocus}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ '& input': { textAlign: 'right' } }}
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1.5 }}>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={line.credit}
                        onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'credit')}
                        onFocus={handleFocus}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ '& input': { textAlign: 'right' } }}
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        onClick={() => removeLine(index)} 
                        color="error" 
                        size="small"
                        disabled={lines.length <= 2 || isView}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 1 }}>
                    {!isView && (
                    <Button 
                      startIcon={<AddIcon />} 
                      onClick={addLine}
                      sx={{ ml: 1 }}
                    >
                      {t('addLine')}
                    </Button>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                <Typography variant="subtitle1" color="text.secondary">{t('totalDebit')}</Typography>
                <Typography variant="h6" fontWeight="bold">{getTotalDebit().toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                <Typography variant="subtitle1" color="text.secondary">{t('totalCredit')}</Typography>
                <Typography variant="h6" fontWeight="bold">{getTotalCredit().toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                <Typography variant="subtitle1" color="text.secondary">{t('difference')}</Typography>
                <Chip 
                  label={getDifference().toLocaleString()} 
                  color={Math.abs(getDifference()) > 0.01 ? 'error' : 'success'}
                  variant={Math.abs(getDifference()) > 0.01 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 'bold', minWidth: 80 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/journal-entries')}
            size="large"
            color="inherit"
          >
            {isView ? t('BACK') : t('CANCEL')}
          </Button>
          {!isView && (
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading || Math.abs(getDifference()) > 0.01}
            size="large"
            sx={{ minWidth: 150 }}
          >
            {loading ? t('saving') : t('SAVE')}
          </Button>
          )}
        </Box>
      </form>
      
      <ImportJournalEntryDialog 
        open={importOpen} 
        onClose={() => setImportOpen(false)} 
        onImport={handleImportSuccess}
      />
    </Box>
  );
};

export default JournalEntryForm;
